/* eslint-disable security/detect-object-injection */
import PaymentLogo from './PaymentLogo';

/**
 * Reusable read-only display for a single payment gateway's commission config.
 * Supports:
 *   - Fixed Percentage: single string value (legacy) or per-brand object (new)
 *   - Value Based Commission: bracket rows
 * Renders nothing if commissionData is absent or has no usable fields.
 */
export default function PaymentCommissionView({ title, methodKey, commissionData }) {
    if (!commissionData) return null;

    const hasData =
        commissionData.items !== undefined ||
        (commissionData.commission_percentage !== undefined && commissionData.commission_percentage !== null);

    if (!hasData) return null;

    const isValueBased = commissionData.type === 'value_based_commision';
    const pct = commissionData.commission_percentage;
    const isPerBrand = typeof pct === 'object' && pct !== null;

    return (
        <div className="bg-[#fbf1f6] flex flex-col gap-5 px-4 py-3 rounded-[12px] w-full mb-5">

            {/* Gateway header: logo + name */}
            <div className="flex gap-2 items-center">
                <PaymentLogo methodKey={methodKey} />
                {title && ['Google Pay', 'PayPal', 'crypto'].includes(methodKey) && <p className="text-base text-black">{title}</p>}
            </div>

            {/* Commission data card */}
            <div className="bg-white border border-[#EB9DC7] rounded-[8px] px-[23px] pt-[31px] pb-[44px]">
                {!isValueBased ? (
                    <>
                        <p className="text-base font-semibold text-[#1f1f1f] mb-[25px]">Fixed Percentage</p>
                        {isPerBrand ? (
                            /* Per-brand percentages (new grouped gateway structure) */
                            <div className="flex flex-wrap gap-x-[80px] gap-y-5">
                                {Object.entries(pct).map(([brandKey, value]) => (
                                    <div key={brandKey}>
                                        <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px] capitalize">
                                            {brandKey.replace(/_/g, ' ')} Commission
                                        </p>
                                        <p className="text-base text-[#1f1f1f]">{value}%</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* Single percentage (legacy / crypto) */
                            <div>
                                <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px]">Commission Percentage</p>
                                <p className="text-base text-[#1f1f1f]">{pct}%</p>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <p className="text-base font-semibold text-[#1f1f1f] mb-[25px]">Value Based Commission</p>
                        {commissionData.items?.map((item, index) => (
                            <div className="flex mb-[39px] last:mb-0" key={index}>
                                <div className="min-w-[226px]">
                                    <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px]">Minimum Cart Value</p>
                                    <p className="text-base text-[#1f1f1f]">{item.min_cart_value}</p>
                                </div>
                                <div className="min-w-[227px]">
                                    <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px]">Maximum Cart Value</p>
                                    <p className="text-base text-[#1f1f1f]">{item.max_cart_value}</p>
                                </div>
                                <div className="min-w-[227px]">
                                    <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px]">Commission Type</p>
                                    <p className="text-base text-[#1f1f1f]">
                                        {item.commission_type === 'commission_percentage'
                                            ? 'Percentage Commission'
                                            : 'Fixed Amount'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[14px] font-semibold text-[#6b6b6b] mb-[11px]">Commission</p>
                                    <p className="text-base text-[#1f1f1f]">
                                        {item.commission
                                            ? item.commission_type === 'commission_percentage'
                                                ? `${item.commission}%`
                                                : item.commission
                                            : '-'}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}
